import React, { useEffect, useMemo, useState } from 'react';

import { ReactComponent as CopyIcon } from 'design-system/assets/copy.svg';
import { ReactComponent as DownloadIcon } from 'design-system/assets/download.svg';
import { ReactComponent as EyeIcon } from 'design-system/assets/eye.svg';
import { ReactComponent as MailIcon } from 'design-system/assets/mail.svg';
import { Checkbox, Menu, Table } from 'design-system';
import { toast } from 'react-toastify';

import { unsecuredCopyToClipboard } from 'utils/functions';

import {
  BankSlipRow,
  formatCompetenceLabel,
  formatBoletoNumberDisplay,
  formatDueDate,
  formatInvoiceNumber,
  formatInstallmentAmount,
  getBoletoNumber,
  getInstallmentStatus,
  getStatusBadgeTheme,
  isAvailableForDownload,
} from '../../utils/bankSlipDisplay';
import StatusCompensationTooltip from './StatusCompensationTooltip';
import {
  ActionsCell,
  ActionsMenuWrap,
  BoletoNumber,
  BoletoNumberCell,
  CheckboxCell,
  CopyButton,
  EmptyMessage,
  MenuTrigger,
  StatusBadge,
  StatusHeaderLabel,
  TableSection,
  ViewButton,
} from './styles';

export type BankSlipSortKey =
  | 'boleto'
  | 'nota'
  | 'valor'
  | 'vencimento'
  | 'competencia';

export type BankSlipTableProps = {
  rows: BankSlipRow[];
  selectedIds: string[];
  selectAllChecked: boolean;
  sortKey: BankSlipSortKey | null;
  sortDirection: 'asc' | 'desc' | null;
  emptyMessage?: string;
  compact?: boolean;
  onToggleSelectAll: () => void;
  onToggleRow: (invoiceId: string, installmentId: string) => void;
  onSort: (key: BankSlipSortKey) => void;
  onView: (row: BankSlipRow) => void;
  onDownload: (row: BankSlipRow) => void;
  onSendEmail: (row: BankSlipRow) => void;
  className?: string;
};

const MenuDotsIcon = (): JSX.Element => (
  <svg viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="3" r="1.25" />
    <circle cx="8" cy="8" r="1.25" />
    <circle cx="8" cy="13" r="1.25" />
  </svg>
);

const BankSlipTable = ({
  rows,
  selectedIds,
  selectAllChecked,
  sortKey,
  sortDirection,
  emptyMessage = 'Nenhum registro disponível!',
  compact = false,
  onToggleSelectAll,
  onToggleRow,
  onSort,
  onView,
  onDownload,
  onSendEmail,
  className,
}: BankSlipTableProps): JSX.Element => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const customerType =
    typeof window !== 'undefined'
      ? localStorage.getItem('@UltragazEmpresarial:customerType')
      : null;

  const selectableRows = useMemo(
    () => rows.filter(({ installment, invoice }) => isAvailableForDownload(installment, invoice)),
    [rows]
  );

  useEffect(() => {
    if (!openMenuId) return undefined;

    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;

      if (!target?.closest('[data-bankslip-menu]')) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const getSortDirection = (key: BankSlipSortKey): 'asc' | 'desc' | null =>
    sortKey === key ? sortDirection : null;

  const handleCopyBoletoNumber = async (typeableLine?: string): Promise<void> => {
    const number = getBoletoNumber(typeableLine);

    if (!number) return;

    try {
      await navigator.clipboard.writeText(number);
    } catch {
      unsecuredCopyToClipboard(number);
    }

    toast.success('Número do boleto copiado com sucesso!');
  };

  const emptyColSpan = compact ? 3 : 8;

  return (
    <TableSection $compact={compact} className={className}>
      <Table
        className="intro-boletos"
        size={compact ? 'sm' : 'md'}
        headerBackgroundColor="#fff"
        headerColor="#575757"
        striped={!compact}
      >
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell align="center" width={compact ? '44px' : '52px'}>
              <CheckboxCell>
                <Checkbox
                  aria-label="Selecionar todos os boletos"
                  checked={selectAllChecked && selectableRows.length > 0}
                  disabled={selectableRows.length === 0}
                  onChange={onToggleSelectAll}
                />
              </CheckboxCell>
            </Table.HeaderCell>
            <Table.HeaderCell
              align={compact ? 'left' : 'center'}
              sortable
              sortDirection={getSortDirection('boleto')}
              onSort={() => onSort('boleto')}
            >
              Nº do boleto
            </Table.HeaderCell>
            {!compact && (
              <>
                <Table.HeaderCell
                  align="center"
                  sortable
                  sortDirection={getSortDirection('nota')}
                  onSort={() => onSort('nota')}
                >
                  Nº da nota
                </Table.HeaderCell>
                <Table.HeaderCell
                  align="center"
                  sortable
                  sortDirection={getSortDirection('valor')}
                  onSort={() => onSort('valor')}
                >
                  Valor
                </Table.HeaderCell>
                <Table.HeaderCell
                  align="center"
                  sortable
                  sortDirection={getSortDirection('vencimento')}
                  onSort={() => onSort('vencimento')}
                >
                  Vencimento
                </Table.HeaderCell>
                <Table.HeaderCell
                  align="center"
                  sortable
                  sortDirection={getSortDirection('competencia')}
                  onSort={() => onSort('competencia')}
                >
                  Competência
                </Table.HeaderCell>
              </>
            )}
            <Table.HeaderCell align={compact ? 'right' : 'center'}>
              <StatusHeaderLabel $compact={compact}>
                Status
                <StatusCompensationTooltip compact={compact} />
              </StatusHeaderLabel>
            </Table.HeaderCell>
            {!compact && (
              <Table.HeaderCell align="center" width="56px" />
            )}
          </Table.Row>
        </Table.Head>

        <Table.Body striped={!compact}>
          {rows.length > 0 ? (
            rows.map((row) => {
              const { invoice, installment } = row;
              const rowKey = installment.installmentId;
              const canDownload = isAvailableForDownload(installment, invoice);
              const isSelected = selectedIds.includes(invoice.invoiceId);
              const status = getInstallmentStatus(installment);
              const badgeTheme = getStatusBadgeTheme(status);
              const boletoNumber = formatBoletoNumberDisplay(installment.typeableLine);

              return (
                <Table.Row
                  key={rowKey}
                  data-selected={compact && isSelected ? 'true' : undefined}
                >
                  <Table.Cell align="center">
                    <CheckboxCell
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <Checkbox
                        aria-label={`Selecionar boleto ${getBoletoNumber(installment.typeableLine)}`}
                        checked={isSelected}
                        disabled={!canDownload}
                        onChange={() =>
                          onToggleRow(invoice.invoiceId, installment.installmentId)
                        }
                      />
                    </CheckboxCell>
                  </Table.Cell>
                  <Table.Cell align={compact ? 'left' : 'center'}>
                    <BoletoNumberCell $compact={compact}>
                      <BoletoNumber
                        $truncated={boletoNumber.isTruncated}
                        title={boletoNumber.isTruncated ? boletoNumber.full : undefined}
                      >
                        {boletoNumber.display}
                      </BoletoNumber>
                      {boletoNumber.full && (
                        <CopyButton
                          type="button"
                          aria-label="Copiar número do boleto"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleCopyBoletoNumber(installment.typeableLine);
                          }}
                        >
                          <CopyIcon aria-hidden="true" />
                        </CopyButton>
                      )}
                      {compact && (
                        <ViewButton
                          type="button"
                          aria-label="Visualizar boleto"
                          onClick={(event) => {
                            event.stopPropagation();
                            onView(row);
                          }}
                        >
                          <EyeIcon aria-hidden="true" />
                        </ViewButton>
                      )}
                    </BoletoNumberCell>
                  </Table.Cell>
                  {!compact && (
                    <>
                      <Table.Cell align="center">
                        {formatInvoiceNumber(invoice.invoiceNumber)}
                      </Table.Cell>
                      <Table.Cell align="center">
                        {formatInstallmentAmount(installment, invoice)}
                      </Table.Cell>
                      <Table.Cell align="center">
                        {formatDueDate(installment.dueDate)}
                      </Table.Cell>
                      <Table.Cell align="center">
                        {formatCompetenceLabel(installment, invoice, customerType)}
                      </Table.Cell>
                    </>
                  )}
                  <Table.Cell align={compact ? 'right' : 'center'}>
                    <StatusBadge
                      $compact={compact}
                      $backgroundColor={badgeTheme.backgroundColor}
                      $borderColor={badgeTheme.borderColor}
                      $color={badgeTheme.color}
                    >
                      {status}
                    </StatusBadge>
                  </Table.Cell>
                  {!compact && (
                    <Table.Cell align="center">
                      <ActionsCell data-bankslip-menu>
                        <ActionsMenuWrap>
                          <Menu
                            open={openMenuId === rowKey}
                            onOpenChange={(open) =>
                              setOpenMenuId(open ? rowKey : null)
                            }
                            align="right"
                            trigger={
                              <MenuTrigger
                                type="button"
                                aria-label="Ações do boleto"
                              >
                                <MenuDotsIcon />
                              </MenuTrigger>
                            }
                            items={[
                              {
                                id: 'download',
                                label: 'Baixar 2ª via',
                                icon: <DownloadIcon aria-hidden="true" />,
                                disabled: !canDownload,
                                onClick: () => onDownload(row),
                              },
                              {
                                id: 'email',
                                label: 'Enviar por e-mail',
                                icon: <MailIcon aria-hidden="true" />,
                                disabled: !canDownload,
                                onClick: () => onSendEmail(row),
                              },
                              {
                                id: 'view',
                                label: 'Visualizar',
                                icon: <EyeIcon aria-hidden="true" />,
                                onClick: () => onView(row),
                              },
                            ]}
                          />
                        </ActionsMenuWrap>
                      </ActionsCell>
                    </Table.Cell>
                  )}
                </Table.Row>
              );
            })
          ) : (
            <Table.Empty colSpan={emptyColSpan}>
              <EmptyMessage>{emptyMessage}</EmptyMessage>
            </Table.Empty>
          )}
        </Table.Body>
      </Table>
    </TableSection>
  );
};

export default BankSlipTable;
